import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import {
  snapshotCreateSnapshot,
  snapshotDeleteSnapshot,
  snapshotDownloadSnapshot,
  snapshotSnapshotsByEngagementId,
} from "backend/api/snapshot.service";
import {
  teamAvailableEmployees,
  teamInviteFirmItTeamMember,
} from "backend/api/team.service";
import { AccessRole } from "backend/model/accessRole";
import { AvailableEmployeesQueryRequest } from "backend/model/availableEmployeesQueryRequest";
import { EngagementRole } from "backend/model/engagementRole";
import { EngagementSnapshot } from "backend/model/engagementSnapshot";
import { InvitedTeamMember } from "backend/model/invitedTeamMember";
import { APTContext } from "src/app/services/aptContext/apt-context";
import { IChangeState } from "src/app/services/edit-watcher.service";
import { APTCodes } from "src/app/workspace/aptCodes";
import { StandardConfirmationDialogRequest } from "src/standard/standard-confirmation-dialog/standard-confirmation-dialog.types";
import { ModalDialogService } from "src/standard/standard-modal-button/showModalDialog";
import { downloadFileFromUrl } from "src/standard/ts/fileUtils";
import { EntityState } from "src/standard/ts/types";
import invariant from "tiny-invariant";

export const SnapshotKeyFieldName = "id" satisfies keyof Snapshot_IRowData;
export type SnapshotKeyFieldType =
  Required<Snapshot_IRowData>[typeof SnapshotKeyFieldName];

@Injectable({
  providedIn: "root",
})
export class SnapshotManagementService {
  private httpClient = inject(HttpClient);
  private aptContext = inject(APTContext);
  private router = inject(Router);
  private modalDialogService = inject(ModalDialogService);

  canViewEngagementSnapshots = computed(() => {
    return (
      this.aptContext.isSnapshotEnabled() && this.aptContext.isSnapshotUser()
    );
  });
  isScreenOpen = signal<boolean>(false);
  engagementId = signal<string>("");
  engagementLabel = signal<string>("");

  //Screen management
  loadEngagementSnapshotScreen(
    engagementId: string,
    engagementLabel: string,
  ): void {
    if (this.router.url != "/ws/") {
      void this.router.navigate(["/ws/"]);
    }
    this.isScreenOpen.set(true);
    this.engagementId.set(engagementId);
    this.engagementLabel.set(engagementLabel);
  }
  async inviteToWorkspace(engagementId: string): Promise<void> {
    const result = await this.fetchAvailableEmployees({
      memberFirmTitle: {
        memberFirmId: this.aptContext.userMemberFirmId() ?? 0,
      },
      filters: [
        {
          field: "employeeId",
          filterQueryOp: "Equals",
          value: this.aptContext.employeeId(),
        },
      ],
    });
    const user = result.find(
      (x) => x.employeeId.toString() === this.aptContext.employeeId(),
    );
    invariant(user);
    const invitedTeamMember: InvitedTeamMember = {
      engagementRole: EngagementRole.ExternalReviewer,
      employeeId: user.employeeId,
      accessRole: AccessRole.ExternalReviewer,
      memberFirmId: user.memberFirmId,
      engagementSpecialistRole: "",
      email: user.email ?? "",
      name: user.displayName ?? "",
      office: user.office ?? "",
      isFirmITSupportTeamMember: true,
    };
    const res = await teamInviteFirmItTeamMember(this.httpClient, {
      request: {
        invitedEmployeesList: [invitedTeamMember],
        engagementId: engagementId,
      },
    });
    if (res?.resultType == "Failed") {
      let title = $localize`Problem detected`;
      let message = $localize`Failed to invite user.`;

      if (res.messages.length > 0) {
        if (res.messages[0]?.code == "Team_0011") {
          title = APTCodes.getMessage("WarningDialogHeader");
        }

        if (res.messages[0]?.message) {
          message = res.messages[0].message;
        }
      }

      await this.modalDialogService.showConfirmationDialog({
        variant: "informational",
        title: title,
        message: message,
        confirmText: APTCodes.getMessage("OkButton"),
      } satisfies StandardConfirmationDialogRequest);
    }
  }

  async fetchAvailableEmployees(request: AvailableEmployeesQueryRequest) {
    return await this.aptContext.getAPTResultOrThrow(
      await teamAvailableEmployees(this.httpClient, {
        request,
      }),
    );
  }

  closeEngagementSnapshotScreen(): void {
    this.isScreenOpen.set(false);
  }

  //Api calls
  public async getEngagementSnapshotsAsync(
    engagementId: string,
    orderBy: string,
  ) {
    return await this.aptContext.getAPTResultOrThrow(
      await snapshotSnapshotsByEngagementId(this.httpClient, {
        request: {
          engagementId: engagementId,
          orderBy: orderBy,
        },
      }),
    );
  }

  public async createEngagementSnapshot(engagementId: string) {
    return await this.aptContext.getAPTResultOrThrow(
      await snapshotCreateSnapshot(this.httpClient, {
        request: {
          engagementId: engagementId,
          engagementSnapshotId: engagementId, //created on backend
        },
      }),
    );
  }

  public async deleteEngagementSnapshot(
    engagementId: string,
    engagementSnapshotId: string,
  ) {
    return await this.aptContext.getAPTResultOrThrow(
      await snapshotDeleteSnapshot(this.httpClient, {
        request: {
          engagementId: engagementId,
          engagementSnapshotId: engagementSnapshotId,
        },
      }),
    );
  }

  public async downloadEngagementSnapshotFile(
    engagementId: string,
    engagementSnapshotId: string,
  ) {
    const result = await this.aptContext.getAPTResultOrThrow(
      await snapshotDownloadSnapshot(this.httpClient, {
        request: {
          engagementId: engagementId,
          engagementSnapshotId: engagementSnapshotId,
        },
      }),
    );

    downloadFileFromUrl(result.downloadUri);
  }
}

export type Snapshot_IRowData = EngagementSnapshot & EntityState & IChangeState;
